import React, { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Label,
  Checkbox,
  Spinner,
} from "@fluentui/react-components";
import {
  MailRegular,
  AttachRegular,
  DeleteRegular,
} from "@fluentui/react-icons";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "react-toastify";
import {
  useSendOfferEmailMutation,
  useSendInvoiceEmailMutation,
  SendEmailRequestDto,
} from "state/queries/useOffers";
import {
  EmailTemplatePlaceholders,
  prepareEmailTemplate,
} from "library/constants/emailTemplates";
import { useUserInfo } from "hooks/useUserRoles";

// Styled Components
const StyledDialogSurface = styled(DialogSurface)`
  max-width: 900px;
  width: 90vw;
  max-height: 90vh;
`;

const StyledDialogBody = styled(DialogBody)`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(90vh - 40px);
  overflow: hidden;
`;

const StyledDialogTitle = styled(DialogTitle)`
  flex-shrink: 0;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  margin: 0;
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
`;

const StyledDialogContent = styled(DialogContent)`
  overflow-y: auto !important;
  overflow-x: hidden;
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px 24px;

  /* Ensure scrollbar is visible */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StyledDialogActions = styled(DialogActions)`
  flex-shrink: 0;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const StyledInput = styled(Input)`
  width: 100%;
` as typeof Input;

const AttachmentSection = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
`;

const AttachmentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const AttachmentTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => theme.semanticColors.bodyBackgroundHovered};
  }
`;

const AttachmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const AttachmentName = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const FileInputWrapper = styled.div`
  margin-top: 8px;
`;

const StyledReactQuill = styled(ReactQuill)`
  /* Force light mode for better readability */
  .ql-container {
    min-height: 300px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
      Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
    background: #ffffff !important;
  }

  .ql-editor {
    font-size: 14px;
    line-height: 1.6;
    background: #ffffff !important;
    color: #000000 !important;
  }

  .ql-toolbar.ql-snow {
    border: 1px solid #d1d1d1;
    border-radius: 4px 4px 0 0;
    background: #f8f8f8 !important;
  }

  .ql-container.ql-snow {
    border: 1px solid #d1d1d1;
    border-top: none;
    border-radius: 0 0 4px 4px;
    background: #ffffff !important;
  }

  /* Ensure toolbar buttons are visible in light mode */
  .ql-stroke {
    stroke: #444 !important;
  }

  .ql-fill {
    fill: #444 !important;
  }

  .ql-picker-label {
    color: #444 !important;
  }

  /* Style for selected/active buttons */
  .ql-toolbar button.ql-active,
  .ql-toolbar .ql-picker-label.ql-active {
    color: #0078d4 !important;
  }

  .ql-toolbar button.ql-active .ql-stroke {
    stroke: #0078d4 !important;
  }

  .ql-toolbar button.ql-active .ql-fill {
    fill: #0078d4 !important;
  }

  /* Hover effects for toolbar buttons */
  .ql-toolbar button:hover,
  .ql-toolbar .ql-picker-label:hover {
    background: #e1e1e1 !important;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

// Types
export interface EmailAttachment {
  name: string;
  url: string;
  included: boolean;
}

export interface SendEmailFormData {
  fromName: string;
  fromEmail: string;
  toEmail: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  attachments: EmailAttachment[];
  additionalFiles: File[];
}

export interface SendEmailRequest {
  from: {
    name: string;
    email: string;
  };
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  attachments: Array<{
    name: string;
    url?: string;
    file?: string; // base64 encoded file content
  }>;
}

interface SendEmailDialogProps {
  open: boolean;
  onClose: () => void;
  offerId: string;
  invoiceId?: string; // Optional - if provided, will use invoice email endpoint
  defaultAttachment?: {
    name: string;
    url: string;
  };
  template: {
    subject: string;
    body: string;
  };
  templateData?: Partial<EmailTemplatePlaceholders>;
  defaultFromName?: string;
  defaultFromEmail?: string;
  defaultToEmail?: string;
  onEmailSent?: (updatedOffer: any) => void; // Callback when email is successfully sent
}

// Rich text editor modules configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "color",
  "background",
  "align",
  "link",
];

export const SendEmailDialog: React.FC<SendEmailDialogProps> = ({
  open,
  onClose,
  offerId,
  invoiceId,
  defaultAttachment,
  template,
  templateData,
  defaultFromName = "",
  defaultFromEmail = "",
  defaultToEmail = "",
  onEmailSent,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const userInfo = useUserInfo();

  // Use the appropriate mutation hook based on whether invoiceId is provided
  const sendOfferEmailMutation = useSendOfferEmailMutation(
    offerId,
    (updatedOffer) => {
      if (onEmailSent) {
        onEmailSent(updatedOffer);
      }
      onClose();
    }
  );

  const sendInvoiceEmailMutation = useSendInvoiceEmailMutation(
    offerId,
    invoiceId || "",
    (updatedOffer) => {
      if (onEmailSent) {
        onEmailSent(updatedOffer);
      }
      onClose();
    }
  );

  // Select the appropriate mutation based on mode
  console.log("[SendEmailDialog] invoiceId:", invoiceId, "offerId:", offerId);
  const sendEmailMutation =
    invoiceId && typeof invoiceId === "string" && invoiceId.trim() !== ""
      ? sendInvoiceEmailMutation
      : sendOfferEmailMutation;
  console.log(
    "[SendEmailDialog] Using mutation:",
    invoiceId && typeof invoiceId === "string" && invoiceId.trim() !== ""
      ? "invoice"
      : "offer"
  );

  // Form state
  const [formData, setFormData] = useState<SendEmailFormData>({
    fromName: "",
    fromEmail: "",
    toEmail: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
    attachments: [],
    additionalFiles: [],
  });

  const isSending = sendEmailMutation.isPending;

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      console.log(
        "[SendEmailDialog] Initializing with defaultAttachment:",
        defaultAttachment
      );

      // Prepare template with placeholders replaced only when dialog opens
      const preparedTemplate = templateData
        ? prepareEmailTemplate(template, templateData)
        : template;

      // Use user info for from name and email if available, otherwise use defaults
      const fromName = userInfo?.userFullName || defaultFromName;
      const fromEmail = userInfo?.userEmail || defaultFromEmail;

      const initialAttachments = defaultAttachment
        ? [{ ...defaultAttachment, included: true }]
        : [];

      console.log("[SendEmailDialog] Setting attachments:", initialAttachments);

      setFormData({
        fromName,
        fromEmail,
        toEmail: defaultToEmail,
        cc: "",
        bcc: "",
        subject: preparedTemplate.subject,
        body: preparedTemplate.body,
        attachments: initialAttachments,
        additionalFiles: [],
      });
    }
  }, [
    open,
    userInfo,
    defaultAttachment,
    template,
    templateData,
    defaultFromName,
    defaultFromEmail,
    defaultToEmail,
  ]); // Reinitialize form when dialog opens or any of these props change

  // Handlers
  const handleInputChange = useCallback(
    (field: keyof SendEmailFormData) => (_event: any, data: any) => {
      const value = data?.value ?? "";
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleBodyChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, body: value }));
  }, []);

  const handleAttachmentToggle = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.map((att, i) =>
        i === index ? { ...att, included: !att.included } : att
      ),
    }));
  }, []);

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      setFormData((prev) => ({
        ...prev,
        additionalFiles: [...prev.additionalFiles, ...files],
      }));
      // Reset input value to allow selecting the same file again
      if (event.target) {
        event.target.value = "";
      }
    },
    []
  );

  const handleRemoveAdditionalFile = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      additionalFiles: prev.additionalFiles.filter((_, i) => i !== index),
    }));
  }, []);

  const handleSend = useCallback(async () => {
    // Validation
    if (!formData.fromName.trim()) {
      toast.error(
        t("qtg.email.validation.fromNameRequired", "From name is required")
      );
      return;
    }
    if (!formData.fromEmail.trim()) {
      toast.error(
        t("qtg.email.validation.fromEmailRequired", "From email is required")
      );
      return;
    }
    if (!formData.toEmail.trim()) {
      toast.error(
        t("qtg.email.validation.toEmailRequired", "To email is required")
      );
      return;
    }
    if (!formData.subject.trim()) {
      toast.error(
        t("qtg.email.validation.subjectRequired", "Subject is required")
      );
      return;
    }
    if (!formData.body.trim() || formData.body === "<p><br></p>") {
      toast.error(
        t("qtg.email.validation.bodyRequired", "Email body is required")
      );
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.fromEmail)) {
      toast.error(
        t("qtg.email.validation.invalidFromEmail", "Invalid from email address")
      );
      return;
    }
    if (!emailRegex.test(formData.toEmail)) {
      toast.error(
        t("qtg.email.validation.invalidToEmail", "Invalid to email address")
      );
      return;
    }

    try {
      // Convert additional files to base64
      const filePromises = formData.additionalFiles.map(
        (file) =>
          new Promise<{
            name: string;
            content: string;
          }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              const base64 = result.split(",")[1] || ""; // Remove data:*/*;base64, prefix
              resolve({
                name: file.name,
                content: base64,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      );

      const encodedFiles = await Promise.all(filePromises);

      // Build request matching SendEmailRequestDto
      const request: SendEmailRequestDto = {
        from: {
          name: formData.fromName.trim(),
          email: formData.fromEmail.trim(),
        },
        to: formData.toEmail.trim(),
        cc: formData.cc.trim() || undefined,
        bcc: formData.bcc.trim() || undefined,
        subject: formData.subject.trim(),
        body: formData.body,
        attachments: [
          // Include selected proposal attachments (with URL)
          ...formData.attachments
            .filter((att) => att.included)
            .map((att) => ({
              name: att.name,
              url: att.url,
            })),
          // Include additional uploaded files (with base64 file content)
          ...encodedFiles.map((file) => ({
            name: file.name,
            file: file.content,
          })),
        ],
      };

      // Call the mutation
      await sendEmailMutation.mutateAsync(request);

      // Success is handled by the mutation hook's onSuccess callback
      // No need to show toast here as it's already shown in the mutation hook
      onClose();
    } catch (error: any) {
      console.error("Error sending email:", error);
      // Error handling is done by the mutation hook
    }
  }, [formData, onClose, t, sendEmailMutation]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !isSending && onClose()}>
      <StyledDialogSurface>
        <StyledDialogBody>
          <StyledDialogTitle>
            <MailRegular style={{ marginRight: "8px" }} />
            {invoiceId &&
            typeof invoiceId === "string" &&
            invoiceId.trim() !== ""
              ? t("qtg.email.titleInvoice", "Send Invoice via Email")
              : t("qtg.email.title", "Send Proposal via Email")}
          </StyledDialogTitle>
          <StyledDialogContent>
            <FormGrid>
              {/* From Section */}
              <TwoColumnRow>
                <FormRow>
                  <Label required>{t("qtg.email.fromName", "From Name")}</Label>
                  <StyledInput
                    value={formData.fromName}
                    onChange={handleInputChange("fromName")}
                    placeholder={t(
                      "qtg.email.fromNamePlaceholder",
                      "Your name"
                    )}
                    disabled
                  />
                </FormRow>
                <FormRow>
                  <Label required>
                    {t("qtg.email.fromEmail", "From Email")}
                  </Label>
                  <StyledInput
                    type="email"
                    value={formData.fromEmail}
                    onChange={handleInputChange("fromEmail")}
                    placeholder={t(
                      "qtg.email.fromEmailPlaceholder",
                      "your.email@example.com"
                    )}
                    disabled
                  />
                </FormRow>
              </TwoColumnRow>

              {/* To Section */}
              <FormRow>
                <Label required>{t("qtg.email.toEmail", "To Email")}</Label>
                <StyledInput
                  data-help="offer-email-to"
                  type="email"
                  value={formData.toEmail}
                  onChange={handleInputChange("toEmail")}
                  placeholder={t(
                    "qtg.email.toEmailPlaceholder",
                    "recipient@example.com"
                  )}
                  disabled={isSending}
                />
              </FormRow>

              {/* CC Section */}
              <FormRow>
                <Label>{t("qtg.email.cc", "CC")}</Label>
                <StyledInput
                  type="email"
                  value={formData.cc}
                  onChange={handleInputChange("cc")}
                  placeholder={t(
                    "qtg.email.ccPlaceholder",
                    "cc@example.com (optional)"
                  )}
                  disabled={isSending}
                />
              </FormRow>

              {/* Subject */}
              <FormRow>
                <Label required>{t("qtg.email.subject", "Subject")}</Label>
                <StyledInput
                  data-help="offer-email-subject"
                  value={formData.subject}
                  onChange={handleInputChange("subject")}
                  placeholder={t(
                    "qtg.email.subjectPlaceholder",
                    "Email subject"
                  )}
                  disabled={isSending}
                />
              </FormRow>

              {/* Attachments Section */}
              {(formData.attachments.length > 0 ||
                formData.additionalFiles.length > 0) && (
                <AttachmentSection>
                  <AttachmentHeader>
                    <AttachmentTitle>
                      <AttachRegular />
                      {t("qtg.email.attachments", "Attachments")}
                    </AttachmentTitle>
                  </AttachmentHeader>
                  <AttachmentList>
                    {/* Proposal Documents */}
                    {formData.attachments.map((attachment, index) => (
                      <AttachmentItem key={`proposal-${index}`}>
                        <AttachmentInfo>
                          <Checkbox
                            checked={attachment.included}
                            onChange={() => handleAttachmentToggle(index)}
                            disabled={isSending}
                          />
                          <AttachmentName>{attachment.name}</AttachmentName>
                        </AttachmentInfo>
                      </AttachmentItem>
                    ))}
                    {/* Additional Files */}
                    {formData.additionalFiles.map((file, index) => (
                      <AttachmentItem key={`additional-${index}`}>
                        <AttachmentInfo>
                          <AttachRegular
                            style={{ fontSize: "16px", opacity: 0.7 }}
                          />
                          <AttachmentName>
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </AttachmentName>
                        </AttachmentInfo>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<DeleteRegular />}
                          onClick={() => handleRemoveAdditionalFile(index)}
                          disabled={isSending}
                        />
                      </AttachmentItem>
                    ))}
                  </AttachmentList>
                  <FileInputWrapper>
                    <Button
                      appearance="subtle"
                      icon={<AttachRegular />}
                      onClick={handleFileSelect}
                      disabled={isSending}
                      size="small"
                    >
                      {t("qtg.email.addMoreFiles", "Add More Files")}
                    </Button>
                    <HiddenFileInput
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFilesChange}
                    />
                  </FileInputWrapper>
                </AttachmentSection>
              )}

              {/* Email Body */}
              <FormRow data-help="offer-email-body">
                <Label required>{t("qtg.email.message", "Message")}</Label>
                <StyledReactQuill
                  theme="snow"
                  value={formData.body}
                  onChange={handleBodyChange}
                  modules={quillModules}
                  formats={quillFormats}
                  readOnly={isSending}
                />
              </FormRow>
            </FormGrid>
          </StyledDialogContent>
          <StyledDialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isSending}
            >
              {t("qtg.email.cancel", "Cancel")}
            </Button>
            <Button
              data-help="offer-email-send"
              appearance="primary"
              onClick={handleSend}
              disabled={isSending}
              icon={isSending ? <Spinner size="tiny" /> : <MailRegular />}
            >
              {isSending
                ? t("qtg.email.sending", "Sending...")
                : t("qtg.email.send", "Send Email")}
            </Button>
          </StyledDialogActions>
        </StyledDialogBody>
      </StyledDialogSurface>
    </Dialog>
  );
};

export default SendEmailDialog;
